import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { CONSTANTS, UPLDOAD_DUPLICATE_ACTION, UPLOAD_FILE_LOCATION_TYPE } from "@constants";
import getFilesInS3 from "@cores/getFilesInS3";
import convertToByte from "@cores/convertToByte";
import UploadableFileGuideTooltip from "../../components/tooltip/UploadableFileGuideTooltip";
import { Dialog, DialogActions, DialogContent, DialogTitle, Button, Typography, Grid } from "@mzc-pdc/ui";
import TooltipButton from "../../components_v2/tooltip-button/tooltip-button";
import { IconClose } from "@mzc-cloudplex/icons";
import theme, { dialogContentGutterSx } from "@styles/theme";
import { useTranslation } from "react-i18next";

const REGEXP_SCHEME = new RegExp("^(s3|https)://", "i");

const DialogSelectS3Files = ({
    open,
    onClose,
    onSubmit,
    onlyLocal,
    maximumSize,
    acceptFileExtension,
    isMultiFileSelect,
    showDuplicationActionPanel,
}) => {
    const { t } = useTranslation();
    const [files, setFiles] = useState([]);
    const [pending, setPending] = useState(false);
    const [selectedFileUrl, setSelectedFileUrl] = useState("");
    const [selectedLocationType, setSelectedLocationType] = useState(UPLOAD_FILE_LOCATION_TYPE.S3);
    const [isShowDuplicationActionPannel, setIsShowDuplicationActionPannel] = useState(
        showDuplicationActionPanel !== undefined ? showDuplicationActionPanel : false,
    );
    const [uploadDuplicateAction, setUploadDuplicateAction] = useState(UPLDOAD_DUPLICATE_ACTION.ASK);

    const stage = useSelector(({ stage }) => stage);
    const project = useSelector(({ project }) => project);
    const createAsset = useSelector(({ createAsset }) => createAsset);

    const onClickAddS3Url = (url) => {
        if (pending || !url) {
            return;
        }

        setPending(true);
        const count = createAsset.originFiles.length;
        const decodedUrl = decodeURIComponent(url).replace("+", " ");
        const name = decodedUrl.substring(decodedUrl.lastIndexOf("/") + 1, decodedUrl.length);
        const selectedFile = {
            key: String(count),
            displayName: `${createAsset.data?.name ?? ""}${name}`,
            name: name,
            url: decodedUrl,
            locationType: UPLOAD_FILE_LOCATION_TYPE.S3,
        };

        const validationError = validateSelectedFile({ url, name: decodedUrl }, null);
        if (validationError !== null) {
            addSelectedFiles([
                {
                    ...selectedFile,
                    validationError,
                },
            ]);
            return;
        }

        getFilesInS3(stage.id, stage.endpoint, decodedUrl, project.id)
            .then((response) => {
                selectedFile.size = Number(response.headers["s3-object-content-length"]);
                selectedFile.type = response.headers["s3-object-content-type"];

                addSelectedFiles([selectedFile]);
            })
            .catch((e) => {
                console.error(`error while trying to get content length`, e);
                let error = CONSTANTS("FILE_VALIDATE_ERROR_MESSAGE").INVALID_FILE;
                if (e.message.indexOf("400") > -1) {
                    error = CONSTANTS("FILE_VALIDATE_ERROR_MESSAGE").NOT_ALLOWED_URL_PATTERN;
                } else if (e.message.indexOf("403") > -1) {
                    error = CONSTANTS("FILE_VALIDATE_ERROR_MESSAGE").UNAUTHORIZED;
                }

                addSelectedFiles([
                    {
                        ...selectedFile,
                        validationError: error,
                    },
                ]);
            });
    };

    const onClickRemoveFile = (index) => {
        // 선택이 안되는 이슈로 file input 강제로 reset
        setFiles(files.filter((v, i) => i !== index));
    };

    const onClickClose = () => {
        onSubmit(files);
        onClose();
    };

    const onChangedUploadDuplicateAction = (value) => {
        setUploadDuplicateAction(value);

        // this.props.onChangedUploadDuplicateAction(value);
    };

    const validateSelectedFile = (file) => {
        if (!file) {
            return null;
        }
        if (!acceptFileExtension.includes("*")) {
            const REGEXP_EXTENSION = new RegExp(`\.(${acceptFileExtension.join("|")})$`, "i");
            if (!REGEXP_EXTENSION.test(file.name)) {
                return CONSTANTS("FILE_VALIDATE_ERROR_MESSAGE").NOT_ALLOWED_EXTENSION;
            }
        }

        if (file.url && !REGEXP_SCHEME.test(file.url)) {
            return CONSTANTS("FILE_VALIDATE_ERROR_MESSAGE").NOT_ALLOWED_URL_PATTERN;
        }

        if (file.size <= 0) {
            return CONSTANTS("FILE_VALIDATE_ERROR_MESSAGE").INVALID_FILE;
        }

        return null;
    };

    const addSelectedFiles = (selectedFiles) => {
        setPending(false);
        setSelectedFileUrl("");
        setFiles(files.concat(selectedFiles));
    };

    const isInvalidFile = (file) => {
        return file.validationError || file.size === 0;
    };

    const disabled = useMemo(() => files.some((file) => isInvalidFile(file)), [files]);

    return (
        <Dialog open={open} onClose={onClose} variant={"basic"} sx={{ ".MuiPaper-root": { width: "100%" } }}>
            <DialogTitle>
                <Grid container>
                    <Typography variant="h3">{t(`common::label::${"Select files"}`)}</Typography>
                    <UploadableFileGuideTooltip
                        type={onlyLocal ? "local" : ""}
                        maximumSize={maximumSize}
                        // supportedFileFormats={"*"}
                    />
                </Grid>
                <TooltipButton
                    circled
                    title={t(`common::label::Close`)}
                    onClickButton={onClose}
                    buttonIcon={<IconClose size={20} htmlColor={theme.palette.secondary[700]} />}
                />
            </DialogTitle>
            {/* DAM-658: v2 와 좌우 여백을 맞춘다 */}
            <DialogContent sx={(muiTheme) => dialogContentGutterSx(muiTheme)}>
                {selectedLocationType === UPLOAD_FILE_LOCATION_TYPE.S3 && (
                    <div className="input-group">
                        <div className={"form-input form-input-with-icon"}>
                            <span className={"icon icon-bg-dark"}>
                                <i className={"sprite sprite-s3"}></i>
                            </span>
                            <input
                                type="url"
                                value={selectedFileUrl}
                                placeholder={t(`common::msg::${"http(s)://~ or s3://~"}`)}
                                onChange={(e) => setSelectedFileUrl(e.target.value)}
                            />
                        </div>
                        <button
                            className={`btn btn-outline-default ${pending ? "btn-is-loading" : ""}`}
                            type="button"
                            disabled={selectedFileUrl === "" || (!isMultiFileSelect && files.length > 0)}
                            onClick={() => {
                                onClickAddS3Url(selectedFileUrl);
                            }}
                        >
                            <span>{t(`common::label::${"Add"}`)}</span>
                        </button>
                    </div>
                )}

                <div
                    className={`${files.length > 0 ? "d-flex justify-content-end" : "box-border"}`}
                    style={{ marginTop: "10px" }}
                >
                    {files.length === 0 && (
                        <div className="empty">
                            <i className={`sprite sprite-empty-select-files`}></i>
                            <p>{t(`common::msg::${"Please select a file to add"}`)}.</p>
                        </div>
                    )}

                    {files.length > 0 && (
                        <div className={"box-scroll"} style={{ width: "100%" }}>
                            <div className="list-files">
                                <ul>
                                    {files.map((file, i) => (
                                        <li key={i} className={isInvalidFile(file) ? "is-error" : ""}>
                                            <div className={"file"}>
                                                <div className="file-info">
                                                    <span className={"file-name"}>
                                                        <span>{file.url}</span>
                                                    </span>
                                                    <span className={"file-size"}>
                                                        - {file.size > 0 ? convertToByte(file.size) : `0MB`}
                                                    </span>
                                                </div>

                                                <button
                                                    type={"button"}
                                                    className={"btn btn-icon-solid file-delete"}
                                                    style={{ zIndex: 3 }}
                                                    onClick={() => {
                                                        onClickRemoveFile(i);
                                                    }}
                                                >
                                                    <i className={"sprite sprite-cancel-fade"}></i>
                                                </button>
                                            </div>

                                            {isInvalidFile(file) && (
                                                <div className={"message-error-upload"}>
                                                    <i className={"sprite sprite-error"}></i>
                                                    <span>
                                                        {!file.validationError && file.size === 0
                                                            ? CONSTANTS("FILE_VALIDATE_ERROR_MESSAGE").NOT_FOUND_FILE
                                                            : file.validationError}
                                                    </span>
                                                </div>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    )}

                    <hr className="transparent" />
                    {isShowDuplicationActionPannel && (
                        <div>
                            <p className="form-label dot text-cancel">
                                {t(`common::label::Handling in case of duplicate files`)}
                            </p>
                            <div className="form-inline">
                                <label htmlFor="radio1" className="form-radio">
                                    <input
                                        id="radio1"
                                        type="radio"
                                        className={`form-check-input`}
                                        checked={uploadDuplicateAction === UPLDOAD_DUPLICATE_ACTION.ASK}
                                        value={UPLDOAD_DUPLICATE_ACTION.ASK}
                                        onChange={(e) => {
                                            onChangedUploadDuplicateAction(UPLDOAD_DUPLICATE_ACTION.ASK);
                                        }}
                                    />
                                    <i></i>
                                    <span>{t(`common::label::Ask`)}</span>
                                </label>
                                <label htmlFor="radio2" className="form-radio">
                                    <input
                                        id="radio2"
                                        type="radio"
                                        className={`form-check-input`}
                                        checked={uploadDuplicateAction === UPLDOAD_DUPLICATE_ACTION.ALWAYS_OVERWRITE}
                                        value={UPLDOAD_DUPLICATE_ACTION.ALWAYS_OVERWRITE}
                                        onChange={(e) => {
                                            onChangedUploadDuplicateAction(UPLDOAD_DUPLICATE_ACTION.ALWAYS_OVERWRITE);
                                        }}
                                    />
                                    <i></i>
                                    <span>{t(`common::label::Always Overwrite`)}</span>
                                </label>
                                <label htmlFor="radio3" className="form-radio">
                                    <input
                                        id="radio3"
                                        type="radio"
                                        className={`form-check-input`}
                                        checked={uploadDuplicateAction === UPLDOAD_DUPLICATE_ACTION.ALWAYS_SKIP}
                                        value={UPLDOAD_DUPLICATE_ACTION.ALWAYS_SKIP}
                                        onChange={(e) => {
                                            onChangedUploadDuplicateAction(UPLDOAD_DUPLICATE_ACTION.ALWAYS_SKIP);
                                        }}
                                    />
                                    <i></i>
                                    <span>{t(`common::label::Always Skips`)}</span>
                                </label>
                            </div>
                        </div>
                    )}
                </div>
            </DialogContent>
            <DialogActions>
                <Button
                    color="secondary"
                    onClick={(e) => {
                        e.stopPropagation();
                        onClose();
                    }}
                    variant="text"
                >
                    {t(`common::label::${"Cancel"}`)}
                </Button>
                <Button color="primary" onClick={() => onClickClose()} disabled={disabled} variant="contained">
                    {t(`common::label::${"Select"}`)}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DialogSelectS3Files;
